import type { PokerGameTile as PokerGameTileProps } from '@ps/poker-games-tile-mappers';
import { RPGTypes } from '@ps/poker-games-tile-mappers';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import { GameType, UserClubRole } from '@swf/core/types/Poker';
import { Image, useHomeGamesWidget } from '@swf/poker';

import WebPokerGame from '../WebPokerGame/WebPokerGame';

import Footer from './Footer/Footer';
import Header from './Header/Header';
import styles from './PokerGameTile.scss';
import SelectGame from './SelectGame/SelectGame';

export interface Props extends PokerGameTileProps {
  newTab?: boolean;
}

export enum EncodedWebEndpointType {
  TABLE_STATIC_DATA = 'HOME_RING_TABLE',
  TOURNAMENT_STATIC_DATA = 'HOME_TOURNAMENT_STRUCTURE'
}

export const getType = (type: Props['type']) =>
  type === RPGTypes.CASH_GAME || type === RPGTypes.SAG
    ? EncodedWebEndpointType.TABLE_STATIC_DATA
    : EncodedWebEndpointType.TOURNAMENT_STATIC_DATA;

interface StarsWeb {
  homegames: {
    openTable: (obj: object | undefined) => void;
    openTourn: (obj: object | undefined) => void;
  };
}

const PokerGameTile: React.FC<Props> = ({
  imageUrl,
  tags,
  link,
  attributes,
  server: serverAddress,
  serverObj: serverObjectName,
  type,
  iconUrl,
  newTab,
  id,
  clubId
}) => {
  const isEmbedded = useSelector(select.site.isEmbedded);
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget();
  const clubs = useSelector(widgetSelect.clubs);
  const activeGames = useSelector(widgetSelect.activeGames);
  const roleForUser = clubs.find((club) => club.clubId === Number(clubId))?.roleForUser || -1;
  const canSelect =
    (roleForUser === UserClubRole.Admin || roleForUser === UserClubRole.Manager) &&
    (type === GameType[GameType['Cash Game']] || type === GameType[GameType['Tournament']]);

  const handleGameClick = () => {
    if (isEmbedded) {
      const starsWeb = window.starsweb as StarsWeb;

      if (link === 'joinTable') {
        starsWeb?.homegames?.openTable({ tableId: id, clubId });
      } else if (link === 'joinTourn') {
        starsWeb?.homegames?.openTourn({ tournId: id, clubId });
      }
    } else {
      widgetDispatch.joinGame(id);
    }
  };

  return (
    <div className={styles.tileContainer} data-testid="game-item">
      <div className={styles.tile} onClick={handleGameClick}>
        <Image
          alt="Game image"
          className={styles.gameImage}
          data-testid="game-item-image"
          imageUrl={imageUrl}
          loaderClassName={styles.loader}
        />
        <Header iconUrl={iconUrl} tags={tags} />
        <Footer attributes={attributes} />
        {activeGames.includes(id) && (
          <WebPokerGame
            id={id}
            serverAddress={serverAddress}
            serverObjectName={serverObjectName}
            type={type}
            newTab={newTab}
          />
        )}
      </div>
      {canSelect ? <SelectGame id={id} /> : null}
    </div>
  );
};

export default PokerGameTile;
